import { Spinner } from "@medusajs/icons"
import { Navigate, Outlet, useLocation } from "react-router-dom"
import { useMe } from "../../../hooks/api/users"
import { hasDistributorAccess } from "../../../lib/vendor-types"
import { SearchProvider } from "../../../providers/search-provider"
import { SidebarProvider } from "../../../providers/sidebar-provider"
import { TalkjsProvider } from "../../../providers/talkjs-provider"

type ProtectedRouteProps = {
  requiredVendorTypes?: string[]
  withProviders?: boolean
}

export const ProtectedRoute = ({
  requiredVendorTypes,
  withProviders = true,
}: ProtectedRouteProps) => {
  const { seller, isPending, error } = useMe()

  const location = useLocation()
  if (isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner className="text-ui-fg-interactive animate-spin" />
      </div>
    )
  }

  if (!seller) {
    return (
      <Navigate
        to={`/login${error?.message ? `?reason=${encodeURIComponent(error.message)}` : ""}`}
        state={{ from: location }}
        replace
      />
    )
  }

  if (requiredVendorTypes?.length) {
    const sellerVendorType = (seller as { vendor_type?: unknown }).vendor_type
    const hasAccess = requiredVendorTypes.some((requiredType) => {
      if (requiredType === "distributor") {
        return hasDistributorAccess(sellerVendorType)
      }

      if (Array.isArray(sellerVendorType)) {
        return sellerVendorType.includes(requiredType)
      }

      return sellerVendorType === requiredType
    })

    if (!hasAccess) {
      return <Navigate to="/dashboard" replace />
    }
  }

  if (!withProviders) {
    return <Outlet />
  }

  return (
    <TalkjsProvider>
      <SidebarProvider>
        <SearchProvider>
          <Outlet />
        </SearchProvider>
      </SidebarProvider>
    </TalkjsProvider>
  )
}
